<section class="flex flex-col gap-5">
  <div class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
    <div>
      <h3 class="flex items-center gap-2 text-base font-bold text-base-content">
        <span class="icon-[mdi--google-analytics] text-2xl text-primary"></span>
        GA4 활성 사용자 현황
      </h3>
      <p class="mt-1 text-sm text-base-content/55">선택 기간의 사용자 페이지 방문 통계입니다.</p>
    </div>

    <div class="flex flex-wrap items-end gap-2">
      <div class="join">
        <button type="button" class="btn btn-sm join-item" (click)="selectPreset(7)">7일</button>
        <button type="button" class="btn btn-sm join-item" (click)="selectPreset(30)">30일</button>
        <button type="button" class="btn btn-sm join-item" (click)="selectPreset(90)">90일</button>
      </div>
      <label class="form-control">
        <span class="label-text text-xs text-base-content/60">시작일</span>
        <input
          type="date"
          class="input input-sm input-bordered"
          [value]="customStartDate()"
          [max]="customEndDate()"
          (input)="updateCustomStartDate($event)" />
      </label>
      <label class="form-control">
        <span class="label-text text-xs text-base-content/60">종료일</span>
        <input
          type="date"
          class="input input-sm input-bordered"
          [value]="customEndDate()"
          [min]="customStartDate()"
          (input)="updateCustomEndDate($event)" />
      </label>
      <button type="button" class="btn btn-sm btn-primary" (click)="applyCustomRange()">적용</button>
    </div>
  </div>

  <aside
    class="flex gap-3 rounded-box border border-info/20 bg-info/5 p-4"
    data-testid="analytics-counting-hint">
    <span class="icon-[mdi--information-outline] shrink-0 text-xl text-info"></span>
    <div class="flex flex-col gap-2 text-xs leading-relaxed text-base-content/60">
      <p class="text-sm font-semibold text-base-content/80">집계 기준 안내</p>
      <p>
        <strong class="text-base-content/80">활성 사용자</strong>
        는 선택 기간 동안 동일 사용자로 인식되면 여러 번 방문해도 1명으로 집계됩니다. 일자별 활성 사용자 수를
        단순히 합산하면 여러 날에 걸쳐 재방문한 사용자가 중복 집계되어 상단 총계보다 높게 나올 수 있습니다.
      </p>
      <p>
        <strong class="text-base-content/80">기기 유형·운영체제</strong>
        는 같은 환경으로 여러 번 접속해도 1명으로 집계됩니다. 다만 한 사용자가 모바일과 PC를 모두 사용하면 각
        항목에 1명씩 포함되므로 항목 합계가 상단 활성 사용자보다 높게 나올 수 있습니다.
      </p>
      <p>
        <strong class="text-base-content/80">진입 페이지</strong>
        는 사용자 수가 아닌 세션 수 기준입니다. 새로운 세션이 시작될 때 시작 페이지가 1회 집계되며, 같은
        사용자라도 재방문으로 새 세션이 생성되면 다시 집계됩니다.
      </p>
    </div>
  </aside>

  @if (loading()) {
    <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <div class="skeleton h-36 w-full"></div>
      <div class="skeleton h-36 w-full"></div>
    </div>
  } @else if (error()) {
    <div class="alert alert-error">
      <span class="icon-[mdi--alert-circle-outline] text-xl"></span>
      <span>GA4 통계를 불러올 수 없습니다.</span>
      <button type="button" class="btn btn-sm" data-testid="analytics-retry" (click)="retry()">다시 시도</button>
    </div>
  } @else if (analytics(); as data) {
    <div class="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <article class="card border border-primary/20 bg-primary/5 xl:col-span-1">
        <div class="card-body justify-center">
          <p class="text-sm text-base-content/60">활성 사용자</p>
          <p class="text-4xl font-bold text-base-content">{{ formatNumber(data.activeUsers) }}명</p>
          <p class="text-xs text-base-content/50">{{ data.startDate }} ~ {{ data.endDate }}</p>
        </div>
      </article>
      <article class="card border border-base-200 bg-base-100 xl:col-span-2">
        <div class="card-body p-4">
          <h4 class="font-semibold text-base-content">일자별 활성 사용자</h4>
          @if (data.daily.length > 0) {
            <lepi-chart [options]="chartOptions()" />
          } @else {
            <p class="py-12 text-center text-sm text-base-content/50">표시할 데이터가 없습니다.</p>
          }
        </div>
      </article>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
      @for (
        breakdown of [
          { title: '기기 유형', items: data.devices },
          { title: '운영체제', items: data.operatingSystems },
        ];
        track breakdown.title
      ) {
        <article class="card border border-base-200 bg-base-100">
          <div class="card-body gap-4 p-5">
            <h4 class="font-semibold text-base-content">{{ breakdown.title }}</h4>
            @for (item of breakdown.items; track item.label) {
              <div class="flex flex-col gap-1.5">
                <div class="flex items-center justify-between gap-3 text-sm">
                  <span class="truncate text-base-content">{{ item.label }}</span>
                  <span class="whitespace-nowrap text-base-content/60">
                    {{ formatNumber(item.activeUsers) }}명 · {{ item.percentage }}%
                  </span>
                </div>
                <progress class="progress progress-primary w-full" [value]="item.percentage" max="100"></progress>
              </div>
            } @empty {
              <p class="py-8 text-center text-sm text-base-content/50">표시할 데이터가 없습니다.</p>
            }
          </div>
        </article>
      }

      <article class="card border border-base-200 bg-base-100">
        <div class="card-body gap-4 p-5">
          <div>
            <h4 class="font-semibold text-base-content">진입 페이지</h4>
            <p class="mt-1 text-xs text-base-content/45">
              선택 기간에 방문자가 처음 진입한 페이지이며, 상위 5개만 표시됩니다.
            </p>
          </div>
          @for (item of data.landingPages; track item.path) {
            <a
              class="flex flex-col gap-1.5 rounded-box focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="analytics-landing-page-link"
              [href]="'https://kt-store.com' + item.path"
              target="_blank"
              rel="noopener noreferrer">
              <div class="flex items-center justify-between gap-3 text-sm">
                <span class="flex min-w-0 items-center gap-1 text-base-content">
                  <span class="truncate">{{ item.label }}</span>
                  <span class="icon-[mdi--open-in-new] shrink-0 text-base-content/45"></span>
                </span>
                <span class="whitespace-nowrap text-base-content/60">
                  {{ formatNumber(item.sessions) }}회 · {{ item.percentage }}%
                </span>
              </div>
              <progress class="progress progress-primary w-full" [value]="item.percentage" max="100"></progress>
            </a>
          } @empty {
            <p class="py-8 text-center text-sm text-base-content/50">표시할 데이터가 없습니다.</p>
          }
        </div>
      </article>
    </div>

    <p class="text-right text-xs text-base-content/45">
      GA4 조회 시각: {{ data.fetchedAt | date: 'yyyy-MM-dd HH:mm' }}
    </p>
  }
</section>
